export const smoothScrollTo = ({
  element = window,
  to = { top: 0, left: 0 },
  duration = 800,
  easing = easings["easeOutCubic"],
  isReducedMotion = false,
  onFinish,
}: {
  element?: Window | HTMLElement;
  to?: { top: number; left: number };
  duration?: number;
  easing?: (t: number) => number;
  isReducedMotion?: boolean;
  onFinish?: () => void;
}) => {
  if (isReducedMotion) {
    element.scrollTo({ top: to.top, left: to.left, behavior: "instant" });
    onFinish?.();
    return;
  }

  const isWindow = element === window;
  const startX = isWindow
    ? window.scrollX
    : (element as HTMLElement).scrollLeft;
  const startY = isWindow ? window.scrollY : (element as HTMLElement).scrollTop;
  const startTime = performance.now();

  function animationFrame(currentTime: DOMHighResTimeStamp) {
    const elapsedTime = currentTime - startTime;
    const progress = Math.min(elapsedTime / duration, 1);
    const easedProgress = easing(progress);

    const newX = startX + (to.left - startX) * easedProgress;
    const newY = startY + (to.top - startY) * easedProgress;

    // Each frame must land instantly on the eased position this loop computes.
    // Omitting `behavior` defaults to "auto", which inherits the element's CSS
    // `scroll-behavior`; if that's `smooth`, the browser starts its own native
    // glide toward a target this loop moves every frame, and the two animations
    // fight — producing visible shake/jitter. "instant" pins each write.
    element.scrollTo({ top: newY, left: newX, behavior: "instant" });

    if (progress < 1) {
      requestAnimationFrame(animationFrame);
    } else {
      onFinish?.();
    }
  }

  requestAnimationFrame(animationFrame);
};

export const easings = {
  linear: (t) => t,
  easeInSine: (t) => -1 * Math.cos(t * (Math.PI / 2)) + 1,
  easeOutSine: (t) => Math.sin(t * (Math.PI / 2)),
  easeInOutSine: (t) => -0.5 * (Math.cos(Math.PI * t) - 1),
  easeInQuad: (t) => t * t,
  easeOutQuad: (t) => t * (2 - t),
  easeInOutQuad: (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
  easeInCubic: (t) => t * t * t,
  easeOutCubic: (t) => {
    const t1 = t - 1;
    return t1 * t1 * t1 + 1;
  },
  easeInOutCubic: (t) =>
    t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,
  easeInQuart: (t) => t * t * t * t,
  easeOutQuart: (t) => {
    const t1 = t - 1;
    return 1 - t1 * t1 * t1 * t1;
  },
  easeInOutQuart: (t) =>
    t < 0.5 ? 8 * t * t * t * t : 1 - 8 * (t - 1) * (t - 1) * (t - 1) * (t - 1),
  easeInQuint: (t) => t * t * t * t * t,
  easeOutQuint: (t) => {
    const t1 = t - 1;
    return 1 + t1 * t1 * t1 * t1 * t1;
  },
  easeInOutQuint: (t) =>
    t < 0.5
      ? 16 * t * t * t * t * t
      : 1 + 16 * (t - 1) * (t - 1) * (t - 1) * (t - 1) * (t - 1),
  easeInExpo: (t) => (t === 0 ? 0 : Math.pow(2, 10 * (t - 1))),
  easeOutExpo: (t) => (t === 1 ? 1 : -Math.pow(2, -10 * t) + 1),
  easeInOutExpo: (t) => {
    if (t === 0 || t === 1) return t;
    const scaledTime = t * 2;
    const scaledTime1 = scaledTime - 1;
    return scaledTime < 1
      ? 0.5 * Math.pow(2, 10 * scaledTime1)
      : 0.5 * (-Math.pow(2, -10 * scaledTime1) + 2);
  },
  easeInCirc: (t) => {
    const scaledTime = t / 1;
    return -1 * (Math.sqrt(1 - scaledTime * t) - 1);
  },
  easeOutCirc: (t) => {
    const t1 = t - 1;
    return Math.sqrt(1 - t1 * t1);
  },
  easeInOutCirc: (t) => {
    const scaledTime = t * 2;
    const scaledTime1 = scaledTime - 2;
    return scaledTime < 1
      ? -0.5 * (Math.sqrt(1 - scaledTime * scaledTime) - 1)
      : 0.5 * (Math.sqrt(1 - scaledTime1 * scaledTime1) + 1);
  },
  easeInBack: (t, magnitude = 1.70158) =>
    t * t * ((magnitude + 1) * t - magnitude),
  easeOutBack: (t, magnitude = 1.70158) => {
    const scaledTime = t / 1 - 1;
    return (
      scaledTime * scaledTime * ((magnitude + 1) * scaledTime + magnitude) + 1
    );
  },
  easeInOutBack: (t, magnitude = 1.70158) => {
    const scaledTime = t * 2;
    const scaledTime2 = scaledTime - 2;
    const s = magnitude * 1.525;
    return scaledTime < 1
      ? 0.5 * scaledTime * scaledTime * ((s + 1) * scaledTime - s)
      : 0.5 * (scaledTime2 * scaledTime2 * ((s + 1) * scaledTime2 + s) + 2);
  },
  easeInElastic: (t, magnitude = 0.7) => {
    if (t === 0 || t === 1) return t;
    const scaledTime = t / 1;
    const scaledTime1 = scaledTime - 1;
    const p = 1 - magnitude;
    const s = (p / (2 * Math.PI)) * Math.asin(1);
    return -(
      Math.pow(2, 10 * scaledTime1) *
      Math.sin(((scaledTime1 - s) * (2 * Math.PI)) / p)
    );
  },
  easeOutElastic: (t, magnitude = 0.7) => {
    if (t === 0 || t === 1) return t;
    const p = 1 - magnitude;
    const scaledTime = t * 2;
    const s = (p / (2 * Math.PI)) * Math.asin(1);
    return (
      Math.pow(2, -10 * scaledTime) *
        Math.sin(((scaledTime - s) * (2 * Math.PI)) / p) +
      1
    );
  },
  easeInOutElastic: (t, magnitude = 0.65) => {
    if (t === 0 || t === 1) return t;
    const p = 1 - magnitude;
    const scaledTime = t * 2;
    const scaledTime1 = scaledTime - 1;
    const s = (p / (2 * Math.PI)) * Math.asin(1);
    return scaledTime < 1
      ? -0.5 *
          (Math.pow(2, 10 * scaledTime1) *
            Math.sin(((scaledTime1 - s) * (2 * Math.PI)) / p))
      : Math.pow(2, -10 * scaledTime1) *
          Math.sin(((scaledTime1 - s) * (2 * Math.PI)) / p) *
          0.5 +
          1;
  },
  easeOutBounce: (t) => {
    const scaledTime = t / 1;
    if (scaledTime < 1 / 2.75) {
      return 7.5625 * scaledTime * scaledTime;
    } else if (scaledTime < 2 / 2.75) {
      const scaledTime2 = scaledTime - 1.5 / 2.75;
      return 7.5625 * scaledTime2 * scaledTime2 + 0.75;
    } else if (scaledTime < 2.5 / 2.75) {
      const scaledTime2 = scaledTime - 2.25 / 2.75;
      return 7.5625 * scaledTime2 * scaledTime2 + 0.9375;
    } else {
      const scaledTime2 = scaledTime - 2.625 / 2.75;
      return 7.5625 * scaledTime2 * scaledTime2 + 0.984375;
    }
  },
  easeInBounce: (t): number => 1 - easings.easeOutBounce(1 - t),
  easeInOutBounce: (t): number =>
    t < 0.5
      ? easings.easeInBounce(t * 2) * 0.5
      : easings.easeOutBounce(t * 2 - 1) * 0.5 + 0.5,
} satisfies Record<string, (t: number, magnitude?: number) => number>;

export type EasingKey = keyof typeof easings;
